/**
 * Copyright (c) Qwoach LLC 2020. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Artem Bogush <artem@qwoach.com> on 8/25/20
 *
 */


import {Type} from "class-transformer";
import QwoachFile from "@shared/Drive/QwoachFile";
import DocSignPage from "@/core/modules/DocSign/data/DocSignPage";
import DocSignElementType from "@/core/modules/DocSign/data/DocSignElementType";
import {AssignToOptions} from "@/core/modules/DocSign/data/DocSignAssignToData";
import {FilterableProperty} from "@shared/Filterable/PropertyDecorators";
import DocSignDocument from "@/core/modules/DocSign/data/DocSignDocument/DocSignDocument";
import {classToPlain, plainToClass} from "@qwoach/class-transformer-wrapper";
import dayjs from "dayjs";
import {QwoachFileType} from "@shared/Drive/QwoachFileType";
import CommonObject from "@shared/CommonObject";
import {ClearanceLevelEnum} from "@shared/Profile/ClearanceLevelEnum.js";
import FilterableItem from "@shared/Filterable/FilterableItem.js";

export default class DocSignTemplateItem extends FilterableItem {


	@FilterableProperty()
	name: string = null

	coachId: string = null

	@Type(() => QwoachFile)
	file: QwoachFile = null

	urlRef: string = null

	@Type(() => DocSignPage)
	pages: DocSignPage[] = []

	ownerProgramId: string = null
	ownerProgramStepId: string = null

	attachedWorkflowId: string = null
	workflowTitle: string = null

	_isProcessing: boolean = false

	isEditable: boolean = true

	clearanceLevel: ClearanceLevelEnum = ClearanceLevelEnum.STANDARD

	type: QwoachFileType = QwoachFileType.LEGAL_DOC

	signatureHashMap = {} //we extract signature values here to reference them by id to save bandwidth as each signature may be represented by a base64 image which adds up quickly when there are many signatures on a page

	constructor() {
		super();
	}

	getCurrentSchemaVersion() {
		return 2
	}

	getSortedPages(){
		return this.sortPages(this.pages)
	}

	sortPages(pages){
		let collator = new Intl.Collator(undefined, {numeric: true, sensitivity: 'base'});
		return pages.sort((l, r) => collator.compare(l.preview.name, r.preview.name))
	}

	setPreviews(previews){

		let pages = []
		previews.forEach(preview => {
			let page = new DocSignPage(preview, [])
			pages.push(page)
		})
		this.pages = this.sortPages(pages)
		this.resetPageNumbering()
	}

	resetPageNumbering(){
		for(let i = 0; i < this.pages.length; ++i){
			let page = this.pages[i]
			page.pageNumber = i
		}
	}

	updatePreviews(previews){
		previews.forEach(preview => {
			let page = this.findPageByPreviewName(preview.name)
			if(!page){
				page = new DocSignPage(preview, [])
				page.pageNumber = this.pages.length
				this.pages.push(page)
			}
			page.preview = preview
		})
		this.pages = this.sortPages(this.pages)
		this.resetPageNumbering()
	}

	findPageByPreviewName(previewName){
		let res = null
		this.pages.forEach(page => {
			if(page.preview.name == previewName){
				res = page
			}
		})
		return res
	}

	forEachElement(callback){
		this.pages.forEach(page => {
			page.elements.forEach(element => callback(element, page))
		})
	}


	checkIfAllFieldsFilledInBy(signerId, notFilledCallback = () => {}){
		let isFilledAll = true
		this.forEachElement((element) => {
			if (element.assignedTo === signerId && !element.isFilledIn() && element.isRequired) {
				isFilledAll = false
				notFilledCallback(element)
			}
		})
		return isFilledAll
	}

	checkRequiredFieldsPresent(){
		let isCoachSignElemPresent = false
		let isClientSignElemPresent = false
		let template = this

		template.forEachElement(element => {
			if(element.itemType == DocSignElementType.SIGNATURE){
				if(element.assignedTo == AssignToOptions.CLIENT){
					isClientSignElemPresent = true
				}
				if(element.assignedTo == AssignToOptions.COACH){
					isCoachSignElemPresent = true
				}
			}
		})

		return {isCoachSignElemPresent, isClientSignElemPresent}
	}

	setCoachDateSignedNow(){
		let template = this

		template.forEachElement(element => {
			if(element.itemType === DocSignElementType.DATE && element.assignedTo === AssignToOptions.COACH){
				element.text = dayjs().format(element.dateFormat || "DD/MM/YYYY")
			}
		})
	}

	getAllCoachTextFields(){
		let fields = []

		this.forEachElement(element => {
			if(element.assignedTo === AssignToOptions.COACH
				&& element.itemType === DocSignElementType.TEXT){
				fields.push(element)
			}
		})
		return fields
	}

	fillInCoachName(name){
		let coachFields = this.getAllCoachTextFields()
		console.log("fillInCoachName: coachFields = ", coachFields )
		coachFields.forEach(field => {
			if(!field.text){
				field.text = name;
			}
		})
	}

	checkCoachFieldsFilledIn(){
		let template = this
		let res = true

		template.forEachElement(element => {
			if(element.assignedTo === AssignToOptions.COACH && element.isRequired){
				if(!element.signatureData && !element.text){
					res = false
				}
			}
		})

		return res
	}

	generateDocUrl(generateDocUrlFn){
		this.urlRef = generateDocUrlFn(this.coachId, this.id)
	}

	getDocUrl(generateDocUrlFn){
		if(!this.urlRef && generateDocUrlFn){
			this.generateDocUrl(generateDocUrlFn)
		}
		return this.urlRef || this.file.url
	}

	getSignUrl(coachId, clientId){

		let generateLink = CommonObject.generateLink//Vue.config.globalProperties.generateLink

		let link =  generateLink("SignDocumentPath", {coachId: coachId, documentId: this.id}, {clientId: clientId})

		console.log("getSignUrl: link = ", link, ", arguments = ", ...arguments)

		return link
	}

	createDocSingDocument(clientProfile, packageItem){
		let docSignDocument = plainToClass(DocSignDocument, classToPlain(this))
		docSignDocument.regenerateId()
		docSignDocument.parentTemplateId = this.id
		docSignDocument.createdAt = new Date().getTime()
		if(clientProfile){
			docSignDocument.assignClientFromClientProfile(clientProfile)
		}
		if(packageItem){
			docSignDocument.assignFromPackageItem(packageItem)
		}
		return docSignDocument
	}

	resetErrorAll(){
		this.forEachElement(element => element.setError(false))
	}

	migrateSchema(oldVersion, newVersion) {
		super.migrateSchema(oldVersion, newVersion)

		if(oldVersion < 2){
			if(this.documentOwnerId && !this.coachId){
				this.coachId = this.documentOwnerId
				this.documentOwnerId = null
			}
		}
	}

	getWorkflowTitle(){
		if(this.attachedWorkflowId && !this.workflowTitle){
			return "IAOTRC Workflow"
		}
		return this.workflowTitle
	}

	optimizeSignatureData(){

		this.forEachElement(element => {
			if (element.itemType === DocSignElementType.SIGNATURE){
				this.optimizeDataFor(element)
			}
		})
	}

	optimizeDataFor(element){
		if(typeof element.signatureData === "string"
			&& element.signatureData.startsWith("data:image")) {
			let existingId = Object.keys(this.signatureHashMap)
				.find(id => this.signatureHashMap[id] === element.signatureData);

			if (existingId) {
				element.signatureData = existingId;
			} else {
				let idCounter = this.getNextAvailableIdCounter()
				let id = `sig_${idCounter}`;
				this.signatureHashMap[id] = element.signatureData;
				element.signatureData = id;
			}
		}
	}

	getNextAvailableIdCounter() {
		const ids = Object.keys(this.signatureHashMap);
		const counters = ids
			.filter(id => id.startsWith('sig_'))
			.map(id => Number(id.replace('sig_', '')));
		const maxCounter = Math.max(0, ...counters);
		return maxCounter + 1;
	}

	applyOptimizedDataTo(element){
		if (element.itemType === DocSignElementType.SIGNATURE
			&& this.signatureHashMap[element.signatureData]) {
			element.signatureData = this.signatureHashMap[element.signatureData];
		}
	}

}
